import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons, provideNgIconLoader } from '@ng-icons/core';
import { lucideCircleAlert, lucideHouse } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, WithLockableFields } from 'app/home/create-quote/form-locking';
import { LockedQuoteAlertComponent } from 'app/home/create-quote/form-locking/locked-quote-alert.component';
import { PropertyTypeQuoteFormInput, PropertyTypeValue } from 'app/home/create-quote/forms/property-details/property-type/property-type.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-property-type',
  imports: [FormCardComponent, NgIconComponent, ReactiveFormsModule, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  templateUrl: './property-type.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({ lucideCircleAlert, lucideHouse }),
    provideNgIconLoader((name) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${name}.svg`, { responseType: 'text' });
    }),
  ],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/property-details/property-type',
  isAutoAdvanced: true,
})
export class QuoteFormPropertyTypeComponent extends BaseQuoteForm<PropertyTypeQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  private optionsStore = inject(QuoteFormOptionsStore);
  public formDataIn: PropertyTypeQuoteFormInput = this.quoteDraftStore.propertyTypeQuoteFormSelector();

  public propertyTypeOptions = this.optionsStore.propertyTypeQuoteFormOptionsSelector();

  public formDefinition = {
    propertyType: new FormControl<PropertyTypeValue | undefined>(undefined, Validators.required),
  };
  public propertyTypeForm = new FormGroup(this.formDefinition);

  public override autoAdvanceControlRef = this.propertyTypeForm.controls.propertyType;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('propertyType')) {
      this.propertyTypeForm.controls.propertyType.setValue(this.formDataIn.propertyType);
    }
  }

  private getFormValues(): Pick<QuoteDraftState, 'propertyType'> {
    const { controls } = this.propertyTypeForm;
    const formValues = {
      propertyType: controls.propertyType.value ?? undefined,
    };

    return formValues;
  }

  public getFormControls() {
    return this.propertyTypeForm.controls;
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.propertyTypeForm.markAllAsTouched();
    if (this.propertyTypeForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public get showError() {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.propertyTypeForm.get('propertyType');
    return !!control?.invalid && !!control?.touched;
  }
}
